/**
 * Обращения к прослойке. Никаких прямых запросов в Traccar отсюда быть не
 * должно: браузер знает только наш адрес и нашу сессионную куку.
 */

import type { ScheduleRun } from './eventName'

/** Сводка по срабатыванию расписания. Тип живёт рядом с её показом. */
export type { ScheduleRun }


export type Identity = {
  id: number
  name: string
  email: string | null
  role: 'admin' | 'dispatcher' | 'manager'
  canBlock: boolean
  canManage: boolean
  /**
   * Администратор сервера (настоящий Traccar-флаг), а не роли admin в
   * приложении: у администратора компании (комнаты) role тоже "admin", но
   * серверных экранов вроде журнала ему предлагать нельзя — они физически
   * не могут показать ему только его данные, Traccar отдаёт их только
   * администратору целиком.
   */
  serverAdmin: boolean
  /** Включён ли вход в два шага. Самого ключа наружу не бывает. */
  twoStep: boolean
}

export type Device = {
  id: number
  name: string
  uniqueId: string
  status: string // online | offline | unknown
  lastUpdate: string | null
  disabled: boolean
  /** Паспорт машины: plate, brand, vehicleModel, tankLiters. Рядом лежит
      расписание — поэтому атрибуты всегда сливаются, а не переписываются. */
  attributes?: Record<string, unknown>
}

export type Position = {
  id: number
  deviceId: number
  latitude: number
  longitude: number
  speed: number // узлы, как отдаёт Traccar
  course: number
  deviceTime: string
  attributes: Record<string, unknown>
}

export type ReportKind = 'route' | 'trips' | 'stops' | 'summary' | 'events'

export type TripRow = {
  deviceId: number
  deviceName: string
  startTime: string
  endTime: string
  startAddress: string | null
  endAddress: string | null
  distance: number      // метры
  averageSpeed: number  // узлы
  maxSpeed: number
}

export type StopRow = {
  deviceId: number
  deviceName: string
  startTime: string
  endTime: string
  duration: number      // миллисекунды
  address: string | null
}

export type SummaryRow = {
  deviceId: number
  deviceName: string
  distance: number
  averageSpeed: number
  maxSpeed: number
  engineHours: number   // миллисекунды
}


export type EventRow = {
  id: number
  deviceId: number
  type: string
  eventTime: string
  /** Есть у событий зоны. По нему въезд и выезд узнают друг друга. */
  geofenceId?: number
  /**
   * У события типа `alarm` здесь лежит подтип: `tow`, `powerCut`, `jamming`,
   * `accident`. Без него все тревоги выглядят одинаково.
   */
  attributes?: Record<string, unknown>
}

export type AppConfig = {
  mapCenter: { lat: number; lon: number }
  defaultLanguage: string
  /** Заведён ли ключ Google: без него адресов и времени в пути нет. */
  maps: boolean
  /** Марки, у которых есть знак. Приходят с сервера, чтобы список выбора и
      проверка при записи не разошлись. */
  brands?: { id: string; name: string }[]
  /** На сколько суток назад сервер отдаёт трек. Оттуда же, где проверяется. */
  trackDays?: number
}

export type Role = 'admin' | 'dispatcher' | 'manager'

/**
 * Зона на карте.
 *
 * circle равен null, если зона нарисована не кругом: в самом Traccar можно
 * начертить многоугольник. Рисовать такие мы не умеем, но и прятать чужую
 * зону нельзя — она видна в списке и просто не появляется на карте.
 */
export type Zone = {
  id: number
  name: string
  description: string
  circle: { latitude: number; longitude: number; radius: number } | null
}

/**
 * Строка журнала блокировок.
 *
 * action равен null, когда трекер не ответил: команда ушла, а подтверждения
 * нет. Это не то же самое, что «заблокировано», и показывать надо именно так.
 */
export type JournalRow = {
  at: string
  who: string | null
  address: string | null
  deviceId: number
  deviceName: string
  action: 'block' | 'unblock' | null
  confirmed: boolean
  result: string | null
}

/**
 * Расписание блокировки машины.
 *
 * running говорит, работает ли расписание СЕЙЧАС, а не включено ли оно в
 * настройках. Ложь здесь дороже молчания: человек настроит ночную блокировку,
 * увидит, что всё в порядке, и будет считать фургон запертым. Поэтому false
 * приходит и когда учётной записи службы нет, и когда последний проход
 * не удался.
 */
export type BlockSchedule = {
  enabled: boolean
  tz: string
  workFrom: string
  workUntil: string
  workDays: number[]
}

export type ScheduleState = { schedule: BlockSchedule | null; running: boolean }

/** Машина, открытая по временной ссылке. Больше о ней наружу не уходит. */
export type SharedVehicle = {
  deviceName: string
  expiresAt: string
  position: {
    latitude: number
    longitude: number
    speed: number
    deviceTime: string | null
  } | null
}

/** Выданная ссылка. Токена Traccar здесь нет и быть не может. */
export type ShareLink = {
  key: string
  deviceId: number
  deviceName: string
  expiresAt: string
  by: string | null
}

/** Состояние приглашения: письмо ушло / не ушло / срок вышел. */
export type InviteState = 'pending' | 'unsent' | 'expired'

/** Пользователь на экране управления. Пароля и токенов здесь нет никогда. */
export type Person = {
  id: number
  name: string
  email: string | null
  role: Role
  disabled: boolean
  invite: InviteState | null
}

/** Чьё приглашение — то, что видит человек, открывший ссылку. */
export type InviteInfo = { name: string; email: string }

/** Компания (комната) на экране управления сервером. */
export type Company = {
  id: number
  company: string
  contact: string
  devices: number
  deviceLimit: number
  people: number
  userLimit: number
  expiresAt: string | null
  disabled: boolean
  canBlock: boolean
  lastSeen: string | null
  createdAt: string | null
  invite?: 'pending' | 'unsent'
}

/** Состояние сервера в целом — для карточки на экране компаний. */
export type Health = {
  version: string
  startedAt: string
  rooms: number
  devices: number
  online: number
  lastPositionAt: string | null
}

/** Строка журнала действий администратора сервера над компаниями. */
export type HostAction = {
  at: string
  action: string
  actor: string
  roomId: number | null
  room: string
  detail: string
}

/** Ошибка с текстом, который уже пригоден для показа человеку. */
export class ApiError extends Error {
  // Поле объявлено отдельно, а не сокращением в списке параметров: Node
  // запускает тесты, просто срезая типы, а сокращение — не тип, а код,
  // который при таком запуске некому породить.
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response
  try {
    response = await fetch(path, {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      ...init,
    })
  } catch {
    // сеть не дошла до сервера — отличается от ответа сервера с ошибкой
    throw new ApiError(0, 'network')
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const body = text ? safeJson(text) : null

  if (!response.ok) {
    const detail =
      body && typeof body === 'object' && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : ''
    throw new ApiError(response.status, detail)
  }
  return body as T
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

export const api = {
  config: () => call<AppConfig>('/api/config'),
  me: () => call<Identity>('/api/me'),
  login: (email: string, password: string, code?: string) =>
    call<Identity>('/api/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, code: code || null }),
    }),

  /** Вход в два шага. Ключ выдаётся, но сохраняется только после подтверждения. */
  startTwoStep: () => call<{ secret: string; uri: string }>('/api/me/totp', { method: 'POST' }),
  confirmTwoStep: (secret: string, code: string) =>
    call<void>('/api/me/totp', { method: 'PUT', body: JSON.stringify({ secret, code }) }),
  stopTwoStep: () => call<void>('/api/me/totp', { method: 'DELETE' }),
  logout: () => call<void>('/api/logout', { method: 'POST' }),
  devices: () => call<Device[]>('/api/devices'),
  block: (deviceId: number) =>
    call<{ blocked: boolean; queued: boolean }>(`/api/devices/${deviceId}/block`, {
      method: 'POST',
    }),
  unblock: (deviceId: number) =>
    call<{ blocked: boolean; queued: boolean }>(`/api/devices/${deviceId}/unblock`, {
      method: 'POST',
    }),

  /**
   * Просит трекер прислать положение прямо сейчас.
   *
   * Ответа с координатами тут нет и быть не может: трекер отвечает не серверу
   * приложения, а Traccar, отдельной записью. Она придёт живой лентой, как
   * любая другая, и карточка обновится сама.
   */
  /**
   * Последние срабатывания расписания — сводкой на экран «События».
   *
   * Живут в памяти службы: доказательство задним числом даёт журнал
   * блокировок, а это оперативная сводка на утро. Перезапуск её теряет.
   */
  scheduleRuns: (since: Date) =>
    call<ScheduleRun[]>(`/api/schedule/runs?since=${since.toISOString()}`),

  /**
   * Адреса по координатам, пачкой.
   *
   * Считает сервер: ключ Google живёт там и в браузер не попадает. Ответы он
   * же и помнит, поэтому повторный вопрос про ту же точку ничего не стоит.
   */
  addresses: (points: { latitude: number; longitude: number }[]) =>
    call<(string | null)[]>('/api/addresses', {
      method: 'POST',
      body: JSON.stringify({ points }),
    }),

  /** Сколько ехать от точки человека до машины. Секунды, с учётом пробок. */
  eta: (deviceId: number, from: { latitude: number; longitude: number }) =>
    call<{ seconds: number }>(`/api/devices/${deviceId}/eta`, {
      method: 'POST',
      body: JSON.stringify(from),
    }),

  /** То же самое для того, кому прислали временную ссылку. Входа не требует. */
  sharedEta: (key: string, from: { latitude: number; longitude: number }) =>
    call<{ seconds: number }>(`/api/share/${key}/eta`, {
      method: 'POST',
      body: JSON.stringify(from),
    }),

  refresh: (deviceId: number) =>
    call<{ requested: boolean; name: string }>(`/api/devices/${deviceId}/refresh`, {
      method: 'POST',
    }),
  positions: () => call<Position[]>('/api/positions'),

  /** Отчёты. Пределы по объёму периода проверяет сервер. */
  report: <T>(kind: ReportKind, deviceIds: number[], since: Date, until: Date) => {
    const q = new URLSearchParams({ since: since.toISOString(), until: until.toISOString() })
    for (const id of deviceIds) q.append('deviceId', String(id))
    return call<T>(`/api/reports/${kind}?${q}`)
  },

  // --- управление: только для администратора --------------------------------
  //
  // Правила — на сервере: IMEI, длина пароля, запрет удалить заблокированную
  // машину и запрет снять права с себя. Здесь их копий нет намеренно: вторая
  // копия правила однажды разойдётся с первой, а отказ и так приходит готовым
  // для показа текстом.

  addDevice: (vehicle: {
    name: string
    imei: string
    plate?: string
    brand?: string
    vehicleModel?: string
    tankLiters?: number
  }) => call<Device>('/api/devices', { method: 'POST', body: JSON.stringify(vehicle) }),
  renameDevice: (
    id: number,
    vehicle: {
      name: string
      plate?: string
      brand?: string
      vehicleModel?: string
      tankLiters?: number
    },
  ) => call<Device>(`/api/devices/${id}`, { method: 'PUT', body: JSON.stringify(vehicle) }),
  removeDevice: (id: number) => call<void>(`/api/devices/${id}`, { method: 'DELETE' }),
  /** Замена трекера: сгорел — поставили новый, машина осталась той же. */
  swapTracker: (id: number, imei: string) =>
    call<Device>(`/api/devices/${id}/tracker`, {
      method: 'PUT',
      body: JSON.stringify({ imei }),
    }),

  people: () => call<Person[]>('/api/users'),
  invitePerson: (name: string, email: string, role: Role) =>
    call<Person>('/api/users/invite', {
      method: 'POST',
      body: JSON.stringify({ name, email, role }),
    }),
  resendInvite: (id: number) => call<Person>(`/api/users/${id}/invite`, { method: 'POST' }),
  revokeInvite: (id: number) => call<void>(`/api/users/${id}/invite`, { method: 'DELETE' }),
  editPerson: (
    id: number,
    change: { name?: string; password?: string; role?: Role; disabled?: boolean },
  ) => call<Person>(`/api/users/${id}`, { method: 'PUT', body: JSON.stringify(change) }),
  removePerson: (id: number) => call<void>(`/api/users/${id}`, { method: 'DELETE' }),

  /** Машины, выданные человеку. */
  personDevices: (id: number) => call<Device[]>(`/api/users/${id}/devices`),

  /** Расписание блокировки машины. */
  schedule: (deviceId: number) => call<ScheduleState>(`/api/devices/${deviceId}/schedule`),
  setSchedule: (deviceId: number, schedule: BlockSchedule) =>
    call<ScheduleState>(`/api/devices/${deviceId}/schedule`, {
      method: 'PUT',
      body: JSON.stringify(schedule),
    }),
  clearSchedule: (deviceId: number) =>
    call<void>(`/api/devices/${deviceId}/schedule`, { method: 'DELETE' }),

  /**
   * Временные ссылки на машину.
   *
   * Наружу уходит только наш случайный ключ: токен Traccar, которым прослойка
   * читает данные, остаётся у неё.
   */
  shares: (deviceId: number) => call<ShareLink[]>(`/api/devices/${deviceId}/shares`),
  share: (deviceId: number, hours: number) =>
    call<ShareLink>(`/api/devices/${deviceId}/share`, {
      method: 'POST',
      body: JSON.stringify({ hours }),
    }),
  unshare: (key: string) => call<void>(`/api/share/${key}`, { method: 'DELETE' }),

  /** Что видно по ссылке. Входа не требует — её открывает человек со стороны. */
  shared: (key: string) => call<SharedVehicle>(`/api/share/${key}`),

  /** Приглашение: кто, и назначить пароль. Сессии здесь нет — как у ссылки. */
  inviteInfo: (token: string) => call<InviteInfo>(`/api/invite/${encodeURIComponent(token)}`),
  acceptInvite: (token: string, password: string) =>
    call<void>(`/api/invite/${encodeURIComponent(token)}`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  /**
   * Журнал блокировок: кто, когда, какую машину и чем это кончилось.
   *
   * Только для администратора, и не по нашему решению: журнал действий
   * Traccar открывает лишь ему.
   */
  journal: (since: Date, until: Date) =>
    call<JournalRow[]>(
      `/api/journal?since=${since.toISOString()}&until=${until.toISOString()}`,
    ),

  /**
   * Подписывает человека на события.
   *
   * Без этого Traccar не отправит ему в живую ленту ничего — события будут
   * исправно писаться в отчёт, а всплывающих окон не будет, и ничем себя это
   * не выдаст.
   */
  subscribe: (id: number) =>
    call<void>(`/api/users/${id}/notifications`, { method: 'POST' }),

  /**
   * Зоны: все доступные либо те, за которыми следит одна машина.
   *
   * Обратного вопроса — «какие машины у этой зоны» — нет намеренно: Traccar
   * такого фильтра не имеет и молча отдаёт весь парк. Связка настраивается
   * со стороны машины и стоит один запрос при любом размере парка.
   */
  zones: (deviceId?: number) =>
    call<Zone[]>('/api/zones' + (deviceId ? `?deviceId=${deviceId}` : '')),
  addZone: (name: string, latitude: number, longitude: number, radius: number) =>
    call<Zone>('/api/zones', {
      method: 'POST',
      body: JSON.stringify({ name, latitude, longitude, radius }),
    }),
  editZone: (
    id: number,
    change: { name?: string; latitude?: number; longitude?: number; radius?: number },
  ) => call<Zone>(`/api/zones/${id}`, { method: 'PUT', body: JSON.stringify(change) }),
  removeZone: (id: number) => call<void>(`/api/zones/${id}`, { method: 'DELETE' }),
  watchZone: (zoneId: number, deviceId: number) =>
    call<void>(`/api/zones/${zoneId}/devices`, {
      method: 'POST',
      body: JSON.stringify({ deviceId }),
    }),
  unwatchZone: (zoneId: number, deviceId: number) =>
    call<void>(`/api/zones/${zoneId}/devices`, {
      method: 'DELETE',
      body: JSON.stringify({ deviceId }),
    }),
  grant: (userId: number, deviceId: number) =>
    call<void>('/api/permissions', { method: 'POST', body: JSON.stringify({ userId, deviceId }) }),
  revoke: (userId: number, deviceId: number) =>
    call<void>('/api/permissions', {
      method: 'DELETE',
      body: JSON.stringify({ userId, deviceId }),
    }),

  // --- компании: только для администратора сервера ---------------------------

  listCompanies: () => call<Company[]>('/api/companies'),
  companyHealth: () => call<Health>('/api/companies/health'),
  createCompany: (b: {
    company: string
    contactEmail: string
    deviceLimit: number
    userLimit: number
    expiresAt: string | null
  }) => call<Company>('/api/companies', { method: 'POST', body: JSON.stringify(b) }),
  editCompany: (
    id: number,
    b: { deviceLimit: number | null; userLimit: number | null; expiresAt: string | null; clearExpiry: boolean },
  ) => call<{ ok: true }>(`/api/companies/${id}`, { method: 'PUT', body: JSON.stringify(b) }),
  disableCompany: (id: number) =>
    call<{ ok: true }>(`/api/companies/${id}/disable`, { method: 'POST' }),
  enableCompany: (id: number) =>
    call<{ ok: true }>(`/api/companies/${id}/enable`, { method: 'POST' }),
  issueCompanyKey: (id: number, name: string) =>
    call<{ key: string; id: number; name: string }>(`/api/companies/${id}/keys`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  revokeCompanyKey: (id: number, keyId: number) =>
    call<void>(`/api/companies/${id}/keys/${keyId}`, { method: 'DELETE' }),
  companyJournal: () => call<HostAction[]>('/api/companies/journal'),
}

/** Traccar отдаёт скорость в узлах. Людям показываем километры в час. */
export function knotsToKmh(knots: number): number {
  return Math.round(knots * 1.852)
}
